import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BlogList } from "../../core/components/BlogList";
import { BlogPostView } from "../../core/components/BlogPost";
import { BlogLayout } from "../../core/components/BlogLayout";
import { BlogPost as BlogPostType } from "../../types/blog.types";
import { getBlogPostBySlug } from "../../core/services/blog/blogService";
import { updatePageSEO, getBlogPostSEO } from "../../core/utils/seo";
import AdSenseLoader from "../../core/components/AdSenseLoader";

interface BlogFlowProps {
  onBack: () => void;
}

// Memoized SEO data to avoid recreating objects
const BLOG_INDEX_SEO = {
  title: 'Blog & Tutorials - Video Editing Tips & Guides | Snip Rush',
  description: 'Learn video editing with our comprehensive blog. Tutorials, tips, and guides for creating viral YouTube Shorts, TikTok videos, and Instagram Reels with Snip Rush.',
  keywords: 'video editing blog, YouTube Shorts tutorial, TikTok tips, Instagram Reels guide, video editing tips, shorts creation guide, viral content tips',
  canonical: 'https://sniprush.com/blog',
  ogTitle: 'Blog & Tutorials - Video Editing Tips & Guides',
  ogDescription: 'Learn video editing with comprehensive tutorials and tips for creating viral shorts on YouTube, TikTok, and Instagram.',
  ogImage: 'https://sniprush.com/logo.png',
  twitterTitle: 'Blog & Tutorials - Video Editing Tips & Guides',
  twitterDescription: 'Learn video editing with comprehensive tutorials and tips for creating viral shorts.',
  twitterImage: 'https://sniprush.com/logo.png'
};

export const BlogFlow = React.memo(({ onBack }: BlogFlowProps) => {
  const [selectedPost, setSelectedPost] = useState<BlogPostType | null>(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Listen for URL changes (including browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    // Add event listener for browser navigation
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listener
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Check if we should show a specific blog post based on URL
  useEffect(() => {
    const loadPost = async () => {
      const path = currentPath;
      if (path.startsWith('/blog/') && path !== '/blog') {
        const slug = path.split('/blog/')[1];
        if (!slug) {
          // Invalid URL, redirect to blog index
          window.history.pushState({}, '', '/blog');
          setSelectedPost(null);
          setLoadError(null);
          setIsLoadingPost(false);
          updatePageSEO(BLOG_INDEX_SEO);
          return;
        }
        
        setIsLoadingPost(true);
        setLoadError(null);
        
        try {
          const post = await getBlogPostBySlug(slug);
          if (post) {
            setSelectedPost(post as BlogPostType);
            setLoadError(null);
            // Update SEO for the specific blog post
            updatePageSEO(getBlogPostSEO(post as BlogPostType));
          } else {
            // If post not found, redirect to blog index
            setLoadError('Post not found');
            setTimeout(() => {
              window.history.pushState({}, '', '/blog');
              setSelectedPost(null);
              setLoadError(null);
              updatePageSEO(BLOG_INDEX_SEO);
            }, 2000);
          }
        } catch (error) {
          console.error('Error loading blog post:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to load blog post';
          setLoadError(errorMessage);
          // If error, redirect to blog index after a delay
          setTimeout(() => {
            window.history.pushState({}, '', '/blog');
            setSelectedPost(null);
            setLoadError(null);
            updatePageSEO(BLOG_INDEX_SEO);
          }, 3000);
        } finally {
          setIsLoadingPost(false);
        }
      } else if (path === '/blog') {
        setSelectedPost(null);
        setLoadError(null);
        setIsLoadingPost(false);
        updatePageSEO(BLOG_INDEX_SEO);
      }
    };
    loadPost();
  }, [currentPath]);

  const handlePostClick = useCallback((post: BlogPostType) => {
    const KEY = 'blog-hard-nav-once';
    const now = Date.now();
    const last = Number(sessionStorage.getItem(KEY) || '0');
    const WINDOW_MS = 1500;
    if (!last || now - last > WINDOW_MS) {
      sessionStorage.setItem(KEY, String(now));
      window.location.assign(`/blog/${post.slug}`);
      return;
    }
  }, []);

  const handleBackToList = useCallback(() => {
    const KEY = 'blog-hard-nav-once';
    const now = Date.now();
    const last = Number(sessionStorage.getItem(KEY) || '0');
    const WINDOW_MS = 1500;
    if (!last || now - last > WINDOW_MS) {
      sessionStorage.setItem(KEY, String(now));
      window.location.assign('/blog');
      return;
    }
  }, []);

  // Ensure no-cache meta tags are present on blog pages
  useEffect(() => {
    const addNoCacheMeta = (httpEquiv: string, content: string) => {
      const meta = document.createElement('meta');
      meta.httpEquiv = httpEquiv;
      meta.content = content;
      document.head.appendChild(meta);
      return meta;
    };
    const m1 = addNoCacheMeta('Cache-Control', 'no-cache, no-store, must-revalidate');
    const m2 = addNoCacheMeta('Pragma', 'no-cache');
    const m3 = addNoCacheMeta('Expires', '0');
    return () => {
      m1.remove();
      m2.remove();
      m3.remove();
    };
  }, []);

  // Memoize the components to prevent unnecessary re-renders
  const blogPostView = useMemo(() => {
    if (!selectedPost) return null;
    return (
      <BlogPostView
        post={selectedPost}
        onBack={handleBackToList}
        onPostClick={handlePostClick}
      />
    );
  }, [selectedPost, handleBackToList, handlePostClick]);

  const blogListView = useMemo(() => (
    <BlogList
      onBack={onBack}
      onPostClick={handlePostClick}
    />
  ), [onBack, handlePostClick]);

  // Show loading state when loading a post
  if (isLoadingPost) {
    return (
      <>
        <AdSenseLoader />
        <BlogLayout onBack={onBack} showBackButton={true}>
          <div className="max-w-3xl mx-auto py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading blog post...</p>
          </div>
        </BlogLayout>
      </>
    );
  }

  // Show error state if there was an error loading
  if (loadError) {
    return (
      <>
        <AdSenseLoader />
        <BlogLayout onBack={onBack} showBackButton={true}>
          <div className="max-w-3xl mx-auto py-12 text-center">
            <p className="text-red-600 mb-4">Error: {loadError}</p>
            <p className="text-gray-600 text-sm">Redirecting to blog list...</p>
          </div>
        </BlogLayout>
      </>
    );
  }

  if (selectedPost) {
    return (
      <>
        <AdSenseLoader />
        {blogPostView}
      </>
    );
  }

  return (
    <>
      <AdSenseLoader />
      {blogListView}
    </>
  );
});

// Add default export
export default BlogFlow;
